import {
  plainMurnAddressFromFormkit,
  plainMurnAddressToFormkit,
  type AddressWithMurn,
  type HasPlainAddressWithMurn,
} from "@modernary/mui/components/fk";
import { formatAddressFields } from "@modernary/mui/utils/filters";

export type ContactMethodKind = "email" | "phone" | "address";

export interface ContactMethod {
  id?: number;
  ownerId?: number;
  kind?: ContactMethodKind | string;
  typeMurn?: string;
  entityMurn?: string;
  isPreferred?: boolean;
  value?: string;
  street?: string;
  poBox?: string;
  city?: string;
  stateMurn?: string;
  zipCode?: string;
  countryMurn?: string;
  /** FormKit AddressInputLookup group; present only while editing address methods. */
  murnAddress?: AddressWithMurn;
}

export type ContactMethodCollection = ContactMethod[] | { _items?: ContactMethod[]; value?: ContactMethod[] };

export interface Person {
  id: number;
  murn?: string;
  firstName?: string;
  preferredName?: string;
  lastName?: string;
  middleName?: string;
  middleInitial?: string;
  title?: string;
  notes?: string;
  organizationName?: string;
  displayName?: string;
  hasDustCard?: boolean | null;
  dustCardNumber?: string;
  dustCardExpirationDate?: string;
  contactMethods?: ContactMethodCollection;
}

export interface PersonCreate {
  firstName?: string;
  preferredName?: string;
  lastName?: string;
  middleName?: string;
  title?: string;
  notes?: string;
  organizationName?: string;
  hasDustCard?: boolean | null;
  dustCardNumber?: string;
  dustCardExpirationDate?: string;
  contactMethods?: ContactMethod[];
}

export interface PersonQuery {
  keywords?: string;
  firstName?: string;
  lastName?: string;
  murn?: string;
  take?: number;
  skip?: number;
}

export function emptyPerson(): PersonCreate {
  return {
    firstName: "",
    preferredName: "",
    lastName: "",
    title: "",
    organizationName: "",
    hasDustCard: false,
    dustCardNumber: "",
    dustCardExpirationDate: "",
    notes: "",
    contactMethods: [emptyContactMethod("email"), emptyContactMethod("phone")],
  };
}

export function emptyContactMethod(kind: ContactMethodKind = "email"): ContactMethod {
  const method: ContactMethod = {
    kind,
    value: "",
    isPreferred: kind !== "address",
    street: "",
    poBox: "",
    city: "",
    zipCode: "",
  };
  if (kind === "address") {
    return contactMethodToFormkit(method);
  }
  return method;
}

export function contactMethodItems(collection?: ContactMethodCollection | null): ContactMethod[] {
  if (!collection) return [];
  if (Array.isArray(collection)) return collection;
  return collection._items ?? collection.value ?? [];
}

export function personDisplayName(person?: {
  displayName?: string;
  preferredName?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
} | null): string {
  if (!person) return "";
  if (person.displayName?.trim()) return person.displayName.trim();
  if (person.name?.trim()) return person.name.trim();
  const preferred = person.preferredName?.trim() || person.firstName?.trim() || "";
  const last = person.lastName?.trim() || "";
  return [preferred, last].filter(Boolean).join(" ");
}

export function inferredKind(method: ContactMethod): string {
  const kind = (method.kind ?? "").toLowerCase();
  if (kind === "email" || kind === "phone" || kind === "address") return kind;
  const typeMurn = String(method.typeMurn ?? "");
  if (/contactemailtype/i.test(typeMurn)) return "email";
  if (/contactphonetype/i.test(typeMurn)) return "phone";
  if (/contactaddresstype/i.test(typeMurn)) return "address";
  if (method.street || method.poBox || method.city || method.zipCode || method.murnAddress) return "address";
  if ((method.value ?? "").includes("@")) return "email";
  return "phone";
}

export function normalizeContactMethod(method: ContactMethod): ContactMethod {
  return {
    ...method,
    kind: inferredKind(method) as ContactMethodKind,
  };
}

export function contactTypeGroupCode(kind?: string): "ContactPhoneType" | "ContactEmailType" | "ContactAddressType" {
  const resolved = inferredKind({ kind } as ContactMethod);
  if (resolved === "email") return "ContactEmailType";
  if (resolved === "address") return "ContactAddressType";
  return "ContactPhoneType";
}

export function preferredContactMethod(
  person?: { contactMethods?: ContactMethodCollection } | null,
  kind: ContactMethodKind = "email",
): ContactMethod | undefined {
  const methods = contactMethodItems(person?.contactMethods).filter((method) => inferredKind(method) === kind);
  if (!methods.length) return undefined;
  return methods.find((method) => method.isPreferred) ?? methods[0];
}

export function preferredContactValue(
  person?: { contactMethods?: ContactMethodCollection } | null,
  kind: ContactMethodKind = "email",
): string {
  const methods = contactMethodItems(person?.contactMethods).filter((method) => inferredKind(method) === kind);
  if (!methods.length) return "";
  const preferred = methods.find((method) => method.isPreferred) ?? methods[0];
  if (kind === "address") {
    return (
      formatAddressFields(
        preferred.street,
        preferred.poBox,
        preferred.city,
        undefined,
        preferred.zipCode,
        undefined,
        true,
      ) ?? ""
    );
  }
  return preferred.value?.trim() ?? "";
}

/** Collapse flat address fields into the `murnAddress` group for AddressInputLookup. */
export function contactMethodToFormkit(method: ContactMethod): ContactMethod {
  const normalized = normalizeContactMethod(method);
  if (inferredKind(normalized) !== "address") return normalized;

  const forAddress = { ...normalized } as ContactMethod & HasPlainAddressWithMurn;
  plainMurnAddressToFormkit(forAddress);
  return forAddress;
}

/** Flatten `murnAddress` back to FE flat fields (`poBox`, etc.). */
export function contactMethodFromFormkit(method: ContactMethod): ContactMethod {
  const normalized = normalizeContactMethod(method);
  if (inferredKind(normalized) !== "address") {
    const { murnAddress: _murnAddress, ...rest } = normalized;
    return rest;
  }

  const forAddress = { ...normalized } as ContactMethod & HasPlainAddressWithMurn;
  if (forAddress.murnAddress) {
    plainMurnAddressFromFormkit(forAddress);
  }
  const { murnAddress: _murnAddress, ...rest } = forAddress;
  return rest;
}

export function personContactMethodsToFormkit(methods?: ContactMethod[] | null): ContactMethod[] {
  return (methods ?? []).map(contactMethodToFormkit);
}

export function personContactMethodsFromFormkit(methods?: ContactMethod[] | null): ContactMethod[] {
  return (methods ?? []).map(contactMethodFromFormkit);
}

export function contactMethodsForSave(methods?: ContactMethod[] | null): ContactMethod[] {
  return personContactMethodsFromFormkit(methods)
    .filter((method) => {
      if (!method.typeMurn) return false;
      const kind = inferredKind(method);
      if (kind === "address") return !!(method.street || method.poBox || method.city || method.zipCode);
      return !!method.value?.trim();
    })
    .map((method) => ({
      ...(typeof method.id === "number" && method.id > 0 ? { id: method.id } : {}),
      kind: inferredKind(method),
      typeMurn: method.typeMurn,
      isPreferred: !!method.isPreferred,
      value: method.value?.trim(),
      street: method.street,
      poBox: method.poBox,
      city: method.city,
      stateMurn: method.stateMurn,
      zipCode: method.zipCode,
      countryMurn: method.countryMurn,
    }));
}
